/**
 * The big moments (05 §2): explosions, birth and death, the gate, the exit, the clock. Pure.
 */

import { midiToHz } from '../notes';
import { between, intBelow, type Rand, vary } from '../rand';
import { adsr, type VoiceSpec } from '../voice';
import { hit, type Sfx, tone } from './kit';

/**
 * A long burst of noise with the resonant filter sweeping down over ~0.8 s, the noise's own
 * pitch falling with it, over a sub-bass thump. A butterfly's is brighter and glitters.
 */
function blast(r: Rand, bright: boolean): VoiceSpec[] {
  const voices: VoiceSpec[] = [
    {
      ...hit('noise', vary(r, bright ? 5200 : 3600, 0.1), 1.0, 0.75),
      to: bright ? 700 : 350,
      glide: 0.8,
      filter: { type: 'lowpass', freq: bright ? 9000 : 6000, to: bright ? 500 : 180, sweep: 0.8, q: bright ? 8 : 5 },
    },
    { ...hit('triangle', vary(r, 90, 0.1), 0.3, 0.45), to: 32, glide: 0.3 },
  ];
  if (bright) {
    const steps = Array.from({ length: 8 }, () => midiToHz(90 + intBelow(r, 13)));
    voices.push({
      ...tone('triangle', steps[0] ?? 2000, 0.3, adsr(0.001, 0.2, 0.5, 0.15), 0.1),
      at: 0.05,
      steps,
      stepTime: 0.03,
    });
  }
  return voices;
}

export const explode: Sfx = (r) => blast(r, false);
export const explodeButterfly: Sfx = (r) => blast(r, true);

/** Played with the explosion: a short falling sawtooth, the life going out. */
export const died: Sfx = (r) => [
  {
    ...tone('sawtooth', vary(r, 880, 0.03), 0.45, adsr(0.005, 0.3, 0.6, 0.15), 0.26),
    to: 98,
    glide: 0.5,
    filter: { type: 'lowpass', freq: 3200, to: 600, q: 4 },
  },
];

/** A crack: a burst of bright noise with a high pulse snapping down under it. */
export const born: Sfx = (r) => [
  {
    ...hit('noise', 14000, 0.08, 0.5),
    filter: { type: 'highpass', freq: 1800, q: 0 },
  },
  {
    ...hit('pulse', vary(r, 1760, 0.03), 0.08, 0.18),
    width: 0.2,
    to: 700,
    glide: 0.08,
  },
];

/** Enough diamonds: a sharp crack, then a pulse chirp sweeping up three octaves. */
export const gateOpen: Sfx = (r) => [
  {
    ...hit('noise', 16000, 0.1, 0.6),
    filter: { type: 'highpass', freq: 2500, q: 0 },
  },
  {
    ...tone('pulse', vary(r, 380, 0.04), 0.22, adsr(0.002, 0.15, 0.7, 0.12), 0.26),
    at: 0.02,
    width: 0.3,
    to: 3000,
    glide: 0.25,
  },
];

/** Into the exit: a door slam - a low boom, a wooden thud and a clack on top. */
export const won: Sfx = (r) => [
  {
    ...hit('noise', vary(r, 900, 0.1), 0.25, 0.6),
    to: 120,
    glide: 0.2,
    filter: { type: 'lowpass', freq: 1500, to: 200, sweep: 0.2, q: 4 },
  },
  { ...hit('triangle', vary(r, 85, 0.05), 0.25, 0.5), to: 38, glide: 0.18 },
  {
    ...hit('noise', 9000, 0.04, 0.25),
    filter: { type: 'highpass', freq: 3000, q: 0 },
  },
];

/** The warning tick's note: C5 with ten seconds left, rising a semitone each second to A5. */
export function tickMidi(secondsLeft: number): number {
  return 72 + Math.max(0, Math.min(9, 10 - secondsLeft));
}

/** A pulse ping per second through the last ten. `n` is the seconds left. */
export const tick: Sfx = (r, n) => [
  {
    ...hit('pulse', midiToHz(tickMidi(n)), 0.2, vary(r, 0.3, 0.05)),
    width: 0.25,
  },
];

/** Out of time: a low buzz, two thin pulses a semitone apart beating against each other. */
export const timeout: Sfx = (r) => {
  const f = between(r, 54, 56);
  const buzz = (hz: number, level: number): VoiceSpec => ({
    ...tone('pulse', hz, 0.7, adsr(0.01, 0.1, 0.85, 0.3), level),
    width: 0.1,
    filter: { type: 'lowpass', freq: 1400, q: 3 },
  });
  return [buzz(f, 0.32), buzz(f * 1.06, 0.2)];
};
